import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Modal, SimpleGrid, Stack, Text, ThemeIcon, UnstyledButton } from '@mantine/core';
import {
  IconFileDescription,
  IconFileText,
  IconHeadphones,
  IconPhoto,
  IconVideo,
  type IconProps,
} from '@tabler/icons-react';
import { type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';

import { BulkUploadType, MEDIA_CONFIG } from '../constants/mediaConfig';
import { formatMaxMediaSizeLabel } from '../constants/uploadLimits';
import { contentTypes } from '../types';

import { Platform } from '@/features/pages';
import { Departments } from '@/types';

type Option = {
  type: contentTypes.ContentType;
  label: string;
  Icon: ComponentType<IconProps>;
};

const OPTIONS: Option[] = [
  { type: 'text', label: 'Text', Icon: IconFileText },
  { type: 'photo', label: 'Photo', Icon: IconPhoto },
  { type: 'video', label: 'Video', Icon: IconVideo },
  { type: 'audio', label: 'Audio', Icon: IconHeadphones },
  { type: 'document', label: 'Document', Icon: IconFileDescription },
];

function getOptionDescription(platform: Platform, type: contentTypes.ContentType) {
  if (type === 'text') {
    return 'Скрипты и текстовые материалы';
  }
  const config = MEDIA_CONFIG[type as BulkUploadType];
  return `${config.formatsHint} · ${formatMaxMediaSizeLabel(platform, type as BulkUploadType)}`;
}

type Props = {
  department: Departments;
  platform: Platform;
  pageId: string;
};

export const PickTypeModal = NiceModal.create(({ platform, pageId }: Props) => {
  const modal = useModal();
  const navigate = useNavigate();

  const handlePick = (type: contentTypes.ContentType) => {
    modal.remove();
    if (type === 'text') {
      navigate(`/content/add/text/${platform}/${pageId}`);
      return;
    }
    if (type === 'photo' || type === 'video' || type === 'audio' || type === 'document') {
      navigate(`/content/add/${type}/${platform}/${pageId}`);
    }
  };

  return (
    <Modal
      centered
      overlayProps={{ opacity: 0.4, blur: 1.5 }}
      opened={modal.visible}
      onClose={() => modal.remove()}
      size="lg"
      padding="lg"
      radius="md"
      styles={{
        body: { paddingTop: 20 },
      }}
      title={
        <Stack gap={4} pt="xs">
          <Text fz="lg" fw={700}>
            Выберите тип контента
          </Text>
          <Text size="sm" c="dimmed">
            Можно загрузить только один тип за раз.
          </Text>
        </Stack>
      }
    >
      <SimpleGrid cols={{ base: 1, xs: 2, sm: 3 }} spacing="md" pt="sm">
        {OPTIONS.map(({ type, label, Icon }) => (
          <UnstyledButton
            key={type}
            onClick={() => handlePick(type)}
            p="md"
            style={{
              borderRadius: 12,
              backgroundColor: 'var(--mantine-color-dark-6)',
              border: '1px solid var(--mantine-color-dark-4)',
              transition: 'background-color 150ms ease, border-color 150ms ease',
            }}
            onMouseEnter={(event) => {
              event.currentTarget.style.borderColor = 'var(--mantine-color-dark-3)';
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.borderColor = 'var(--mantine-color-dark-4)';
            }}
          >
            <Stack gap="sm" align="flex-start">
              <ThemeIcon variant="light" radius="md" size={42} color="blue">
                <Icon size={22} stroke={1.5} />
              </ThemeIcon>
              <Stack gap={4}>
                <Text fw={700}>{label}</Text>
                <Text size="xs" c="dimmed">
                  {getOptionDescription(platform, type)}
                </Text>
              </Stack>
            </Stack>
          </UnstyledButton>
        ))}
      </SimpleGrid>
    </Modal>
  );
});
